import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@ui/Icon';
import { MuiIconButton } from '@ui/base_library';
import Tooltip from 'components/Tooltip';
import useAudioPlayer from '../../../../lib/useAudioPlayer';
import CircularLoader from '../../../LoadingIndicators/CircularLoader';
import useSelectedPreviewActor from '../../../avatar/hooks/useSelectedPreviewActor';

export interface Preview {
  src: string;
  loading: boolean;
  handleInitialLoad: (
    isRespelling?: boolean,
    originalText?: string,
    location?: string
  ) => Promise<void>;
  text: string;
}
export interface PlaybackProps {
  label: string;
  preview: Preview;
  className?: string;
  skipSelection?: boolean;
  isRespelling?: boolean;
  original?: Preview;
  onLoading?: (isLoading: boolean) => void;
  setAudioPlaying?: (isPlaying: boolean) => void;
  /**
   * Where the replacement is loading from
   */
  location?: string;
  /**
   * When set, the play button is visually disabled and shows this tooltip text.
   */
  disabledTooltip?: string;
}

interface InnerAudioProps {
  className: PlaybackProps['className'];
  src: Preview['src'];
  setAudioPlaying?: (isPlaying: boolean) => void;
}

const AUDIO_PLAYER_STATE_PAUSED = 'PAUSED';

/**
 * @description Component which leverages the useAudioPlayer hook
 * to play audio given a source
 * @param {InnerAudioProps} InnerAudioProps
 */
const InnerAudio = ({ src, className, setAudioPlaying }: InnerAudioProps) => {
  const [hasPlayedOnce, setHasPlayedOnce] = useState<boolean>(false);
  const [data, audio] = useAudioPlayer({ src, setAudioPlaying });
  const isPaused = data?.state === AUDIO_PLAYER_STATE_PAUSED;

  useEffect(() => {
    if (!hasPlayedOnce && isPaused) {
      data.togglePlayback({ shouldResetIfPaused: true });
      setHasPlayedOnce(true);
    }
  }, [audio, data, hasPlayedOnce, isPaused]);

  return (
    <MuiIconButton
      onClick={() => {
        data.togglePlayback({ shouldResetIfPaused: true });
      }}
      className={className}
    >
      <Icon type={isPaused ? 'playArrow' : 'pause'} cursor="pointer" filled />
    </MuiIconButton>
  );
};

/**
 * @description Hook that handles re-render during inflight request and selection
 */
const useHandleInitialLoad = (
  handleInitialLoad: PlaybackProps['preview']['handleInitialLoad'],
  skipSelection: boolean,
  location?: string
) => {
  const { compositeId } = useSelectedPreviewActor();
  const [internalId, setInternalId] = useState<string | undefined>();
  const manageInitialLoad = useCallback(
    (isRespelling?: boolean, originalText?: string) => {
      if (skipSelection) {
        handleInitialLoad(isRespelling, originalText, location);
        return;
      }
      setInternalId(compositeId);
      handleInitialLoad(isRespelling, originalText, location);
    },
    [skipSelection, compositeId, handleInitialLoad, location]
  );
  const shouldReRender = internalId && internalId !== compositeId;
  return {
    manageInitialLoad,
    shouldReRender,
  };
};

/**
 * @description Component which either either provides a player to
 * load the audio given some text or the current player with audio
 * @param {PlaybackProps} PlaybackProps
 */
const Playback = ({
  preview: { loading, src, handleInitialLoad, text },
  label,
  className = '',
  skipSelection = false,
  isRespelling = false,
  original = undefined,
  onLoading = () => {},
  setAudioPlaying = () => {},
  location,
  disabledTooltip,
}: PlaybackProps) => {
  const { manageInitialLoad, shouldReRender } = useHandleInitialLoad(
    handleInitialLoad,
    skipSelection,
    location
  );

  useEffect(() => {
    onLoading(loading);
  }, [loading, onLoading]);

  const noAudioLoaded = (!loading && src.length === 0) || shouldReRender;
  if (noAudioLoaded) {
    // disabledTooltip only needs to be handled here: typing resets src to '',
    // so InnerAudio is never reached while disabledTooltip could apply.
    return (
      <Tooltip text={disabledTooltip} disabled={!disabledTooltip}>
        <span>
          <MuiIconButton
            aria-label={label}
            disabled={!!disabledTooltip || text.length === 0}
            onClick={() => manageInitialLoad(isRespelling, original?.text)}
            className={className}
          >
            <Icon type="playArrow" filled cursor="pointer" />
          </MuiIconButton>
        </span>
      </Tooltip>
    );
  }
  return loading ? (
    <MuiIconButton disabled className={className} aria-label={label}>
      <CircularLoader size={16} />
    </MuiIconButton>
  ) : (
    <InnerAudio
      src={src}
      className={className}
      setAudioPlaying={setAudioPlaying}
    />
  );
};

export default Playback;
